import React, { useEffect, useMemo, useState } from "react";
import { Bot, Send, Sparkles, User } from "lucide-react";

import { maskPII } from "@/lib/security";
import { useActiveAgentId, useAgentsStore } from "@/lib/agents-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

interface Message {
  role: "agent" | "user";
  content: string;
}

interface AgentQuickTestProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AgentQuickTest({ open, onOpenChange }: AgentQuickTestProps) {
  const activeId = useActiveAgentId();
  const agents = useAgentsStore((state) => state.agents);
  const activeAgent = useMemo(() => agents.find((agent) => agent.id === activeId) ?? null, [agents, activeId]);

  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);

  useEffect(() => {
    if (open && activeAgent) {
      setMessages([
        {
          role: "agent",
          content: `Xin chào, tôi là ${activeAgent.displayName}. Tôi đang chạy ở chế độ kiểm thử cục bộ an toàn cho vai trò ${activeAgent.role}.`,
        },
      ]);
    }
  }, [open, activeAgent]);

  const handleSend = () => {
    if (!input.trim() || !activeAgent) return;

    const userMessage = input.trim();
    setInput("");

    const response = [
      `Đã ghi nhận yêu cầu: "${userMessage}".`,
      `Phản hồi được dựng theo vai trò ${activeAgent.role} với model ${activeAgent.model}.`,
      "Đây là quick test cục bộ để kiểm tra prompt, tone và cơ chế che dữ liệu nhạy cảm trước khi nối backend thật.",
    ].join(" ");

    setMessages((current) => [
      ...current,
      { role: "user", content: userMessage },
      { role: "agent", content: maskPII(response) },
    ]);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col border-white/10 p-0 shadow-2xl sm:max-w-md">
        <SheetHeader className="border-b border-white/5 bg-muted/20 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary">
              <Bot className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <SheetTitle className="text-lg">Kiểm thử: {activeAgent?.displayName}</SheetTitle>
              <p className="text-xs text-muted-foreground">
                {activeAgent?.model} • {activeAgent?.role}
              </p>
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 p-6">
          <div className="space-y-6">
            {messages.map((message, index) => (
              <div key={index} className={`flex gap-3 ${message.role === "user" ? "flex-row-reverse" : ""}`}>
                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                    message.role === "user" ? "bg-secondary" : "bg-primary"
                  }`}
                >
                  {message.role === "user" ? (
                    <User className="h-4 w-4" />
                  ) : (
                    <Bot className="h-4 w-4 text-primary-foreground" />
                  )}
                </div>

                <div
                  className={`max-w-[80%] rounded-2xl p-3 text-sm leading-relaxed ${
                    message.role === "user"
                      ? "rounded-tr-none bg-primary text-primary-foreground"
                      : "rounded-tl-none border border-white/5 bg-muted/50"
                  }`}
                >
                  {message.content}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="border-t border-white/5 bg-muted/20 p-6">
          <div className="mb-3 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-green-500">
            <Sparkles className="h-3 w-3" /> PII masking active
          </div>

          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                placeholder="Nhập nội dung để kiểm thử agent..."
                className="h-11 bg-background pr-10"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleSend();
                }}
              />
              <Sparkles className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" />
            </div>

            <Button className="h-11 w-11 shrink-0" onClick={handleSend} disabled={!input.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </div>

          <p className="mt-4 text-center text-[10px] text-muted-foreground">
            Môi trường kiểm thử cục bộ • Không tiêu hao token thật.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
